import { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { getProductScope } from '@discover-frontend/utils';

import { mapHistoryItemToStanleyResponse } from '../mapHistoryItemToStanleyResponse';
import { STANLEY_QUERY_KEYS } from '../constants';
import { postStanleyQuerySuggestions } from '../request';
import {
  StanleyQueryHistoryItemResponse,
  StanleyQueryRequestPayload,
  StanleyQueryResponse,
} from '../types';

export const useStanleyQueryData = () => {
  const queryClient = useQueryClient();
  const scope = getProductScope();
  const creditsKey = [STANLEY_QUERY_KEYS.GET_CREDITS, scope];
  const historyKey = [STANLEY_QUERY_KEYS.GET_HISTORY, scope];
  const [historyData, setHistoryData] = useState<StanleyQueryResponse | null>(
    null
  );

  const { mutate, mutateAsync, isLoading, isError, data, error, reset } =
    useMutation(
      [STANLEY_QUERY_KEYS.POST_SUGGESTIONS],
      (payload: StanleyQueryRequestPayload) =>
        postStanleyQuerySuggestions(payload),
      {
        onSuccess: () => {
          setHistoryData(null);
          queryClient.invalidateQueries(creditsKey);
          queryClient.invalidateQueries(historyKey);
        },
      }
    );

  const loadFromHistory = useCallback(
    (item: StanleyQueryHistoryItemResponse) => {
      reset();
      setHistoryData(mapHistoryItemToStanleyResponse(item));
    },
    [reset]
  );

  const resetStanleyQuery = useCallback(() => {
    reset();
    setHistoryData(null);
  }, [reset]);

  const mutateStanleyQuery = useCallback(
    (payload: StanleyQueryRequestPayload) => {
      setHistoryData(null);
      mutate(payload);
    },
    [mutate]
  );

  return {
    data: data ?? historyData ?? null,
    mutate: mutateStanleyQuery,
    mutateAsync,
    isLoading,
    isError,
    error,
    reset: resetStanleyQuery,
    loadFromHistory,
  };
};
